import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Mail, Trash2 } from "lucide-react";

/**
 * One row in the notification-preferences dialog. Pure presentational —
 * parent owns the rule data and passes change/remove callbacks.
 *
 * Scope filtering was removed: when the configured event fires for a rule,
 * delivery is controlled by the email and push toggles below.
 */
export default function NotificationRuleRow({
  rule,
  index,
  catalog,
  userEmail,
  onChange,
  onRemove,
}) {
  const isCustom = rule.event_type === "custom";
  const emailEnabled = rule.email_enabled !== false;
  const pushEnabled = rule.push_enabled !== false;
  const update = (patch) => onChange(rule.id, patch);

  return (
    <div
      className="border border-[#E5E7E2] dark:border-white/10 rounded-md p-4 space-y-3"
      data-testid={`notif-rule-${index}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Switch
            checked={!!rule.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
            data-testid={`notif-rule-enabled-${index}`}
          />
          <span className="text-sm font-medium">{rule.enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(rule.id)}
          data-testid={`notif-rule-remove-${index}`}
        >
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Event</Label>
          <Select value={rule.event_type} onValueChange={(v) => update({ event_type: v })}>
            <SelectTrigger data-testid={`notif-rule-event-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {catalog.map((ev) => <SelectItem key={ev.key} value={ev.key}>{ev.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Label (optional)</Label>
          <Input
            value={rule.label || ""}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="e.g. BLM high-priority reports"
            data-testid={`notif-rule-label-${index}`}
          />
        </div>

        <div className="space-y-2 rounded-md border border-[#E5E7E2] dark:border-white/10 p-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> Email
            </Label>
            <Switch
              checked={emailEnabled}
              disabled={!rule.enabled}
              onCheckedChange={(v) => update({ email_enabled: v })}
              data-testid={`notif-rule-email-enabled-${index}`}
            />
          </div>
          <Input
            type="email"
            value={rule.email || ""}
            onChange={(e) => update({ email: e.target.value })}
            placeholder={userEmail || "you@example.com"}
            disabled={!rule.enabled || !emailEnabled}
            data-testid={`notif-rule-email-${index}`}
          />
        </div>

        <div className="space-y-2 rounded-md border border-[#E5E7E2] dark:border-white/10 p-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" /> Mobile push
            </Label>
            <Switch
              checked={pushEnabled}
              disabled={!rule.enabled}
              onCheckedChange={(v) => update({ push_enabled: v })}
              data-testid={`notif-rule-push-enabled-${index}`}
            />
          </div>
          <p className="text-[11px] leading-4 text-[#647069] dark:text-white/60">
            Sends to signed-in mobile devices when push is enabled for the account.
          </p>
        </div>

        {isCustom && (
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Keyword filter (only for Custom events)</Label>
            <Input
              value={rule.keyword || ""}
              onChange={(e) => update({ keyword: e.target.value })}
              placeholder="Match against project name/number or task title"
              data-testid={`notif-rule-keyword-${index}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
